import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import '../styles/Notifications.css';

interface Notification {
  id: string;
  message: string;
  type: string;
  timestamp: string;
}

const NotificationButton: React.FC = () => {
  const { user, token } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activePopup, setActivePopup] = useState<Notification | null>(null);
  const lastNotifIdRef = useRef<string | null>(null);
  const isFirstLoadRef = useRef(true);

  const fetchNotifications = () => {
    if (user && token) {
      fetch(`${API_URL}/api/notifications/${user.email}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            const sorted = data.reverse();
            setNotifications(sorted);
            
            // Check for new notification to show popup
            if (sorted.length > 0) {
              const newest = sorted[0];
              if (!isFirstLoadRef.current && newest.id !== lastNotifIdRef.current) {
                setActivePopup(newest);
                // Auto-hide popup after 5 seconds
                setTimeout(() => setActivePopup(null), 5000);
              }
              lastNotifIdRef.current = newest.id;
            }
            isFirstLoadRef.current = false;
          } else {
            setNotifications([]);
          }
        })
        .catch(err => console.error('Error fetching notifications:', err));
    }
  };

  const deleteNotification = (id: string) => {
    if (token) {
      fetch(`${API_URL}/api/notifications/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(() => fetchNotifications())
        .catch(err => console.error('Error deleting notification:', err));
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5000); // Check every 5 seconds for faster feedback
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className="notification-wrapper">
      {/* REAL-TIME POPUP TOAST */}
      {activePopup && (
        <div className={`notification-popup-toast ${activePopup.type.toLowerCase()}`} onClick={() => { setShowDropdown(true); setActivePopup(null); }}>
          <div className="popup-icon">🔔</div>
          <div className="popup-content">
            <strong>New Update!</strong>
            <p>{activePopup.message}</p>
          </div>
          <button className="popup-close" onClick={(e) => { e.stopPropagation(); setActivePopup(null); }}>×</button>
        </div>
      )}

      <button className="notification-bell" onClick={() => setShowDropdown(!showDropdown)}>
        🔔 {notifications?.length > 0 && <span className="notification-badge">{notifications.length}</span>}
      </button>

      {showDropdown && (
        <div className="notification-dropdown">
          <div className="dropdown-header">
            <h3>Notifications</h3>
            <button className="close-dropdown" onClick={() => setShowDropdown(false)}>×</button>
          </div>
          {notifications?.length === 0 ? (
            <p className="no-notifs">No new updates.</p>
          ) : (
            <div className="notif-list">
              {notifications?.map(n => (
                <div key={n.id} className={`notif-item ${n.type.toLowerCase()}`}>
                  <div className="notif-content">
                    <p>{n.message}</p>
                    <button 
                      className="clear-notif-btn" 
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(n.id);
                      }}
                    >
                      Clear
                    </button>
                  </div>
                  <small>{new Date(n.timestamp).toLocaleTimeString()}</small>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationButton;
